import CircularProgressLoader from 'components/shared/loader/CircularProgressLoader';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useJobProgressTracker from 'hooks/useJobProgressTracker';

const COMPLETION_DELAY_MS = 1000; // ensures the progress bar visibly reaches 100% before onComplete fires

const useJobProgressLoader = (service, { onComplete }) => {
  const [progress, setProgress] = useState(0);

  const jobTracker = useJobProgressTracker(service, {
    progressCallback: (data) => {
      if (data.progress) {
        setProgress(data.progress);
      }
    },
  });

  const { isWarning, isSuccess, isError, data, error } = jobTracker;

  useEffect(() => {
    let timeout;

    const notify = () => {
      const effectiveData = data ?? error?.data;

      onComplete?.({
        severity: effectiveData?.statusLevel ?? 'error',
        status: effectiveData?.status,
        isWarning,
        data: effectiveData,
        error: isError ? error : null,
      });
    };

    if (isSuccess) {
      // timeout is needed to make sure the progress is at 100%
      timeout = setTimeout(notify, COMPLETION_DELAY_MS);
    } else if (isError) {
      notify();
    }

    return () => clearTimeout(timeout);
  }, [isWarning, isSuccess, isError, data, error, onComplete]);

  const start = useCallback(
    (...args) => {
      jobTracker.execute(...args);
    },
    [jobTracker]
  );

  const Loader = useMemo(
    () =>
      function JobProgressLoader(props) {
        return <CircularProgressLoader value={progress} {...props} />;
      },
    [progress]
  );

  return { start, Loader };
};

export default useJobProgressLoader;
